import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowUpward,
    ArrowDownward,
    Folder as FolderIcon
} from "@mui/icons-material";
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import {
    getCollections,
    getCollectionRecords,
    updateRecord,
    //createRecord,
    deleteRecord,
} from "../../services/api";

/* ------------------------------------------------------------------ */
/*  Helper: convert Mongo ObjectId to string & vice‑versa               */
/* ------------------------------------------------------------------ */
const stringifyId = (doc) => ({
    ...doc,
    _id: doc._id?.$oid || doc._id?.toString() || doc._id,
});

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */
export default function MongoBrowser() {
    /* ---------- State ---------- */
    const [collections, setCollections] = useState([]);
    const [selectedColl, setSelectedColl] = useState(null);

    const [records, setRecords] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0); // 0‑basiert für MUI
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [sortField, setSortField] = useState("_id");
    const [sortOrder, setSortOrder] = useState("asc");

    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState(""); // simple JSON string

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Edit‑Dialog
    const [editOpen, setEditOpen] = useState(false);
    const [editDoc, setEditDoc] = useState(null);
    const [editJson, setEditJson] = useState("");

    /* ---------- Load Collections (once) ---------- */
    useEffect(() => {
        const fetch = async () => {
            try {
                const data = await getCollections();
                setCollections(data);
                if (data.length > 0) setSelectedColl(data[0]);
            } catch (e) {
                setError(e.message);
            }
        };
        fetch();
    }, []);

    /* ---------- Load Records whenever query changes ---------- */
    useEffect(() => {
        if (!selectedColl) return;
        const fetch = async () => {
            setLoading(true);
            setError(null);
            try {
                const payload = {
                    page: page + 1, // API ist 1‑basiert
                    limit: rowsPerPage,
                    sort: sortField,
                    order: sortOrder,
                    search,
                    filter,
                };
                const { docs, total: cnt } = await getCollectionRecords(
                    selectedColl,
                    payload
                );
                setRecords(docs.map(stringifyId));
                setTotal(cnt);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [
        selectedColl,
        page,
        rowsPerPage,
        sortField,
        sortOrder,
        search,
        filter,
    ]);

    /* ---------- UI Helpers ---------- */
    const handleSortToggle = (field) => () => {
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const openEdit = (doc) => {
        setEditDoc(doc);
        setEditJson(JSON.stringify(doc, null, 2));
        setEditOpen(true);
    };

    const closeEdit = () => {
        setEditOpen(false);
        setEditDoc(null);
    };

    const saveEdit = async () => {
        try {
            const parsed = JSON.parse(editJson);
            // keep original _id
            const payload = { ...parsed, _id: editDoc._id };
            await updateRecord(selectedColl, editDoc._id, payload);
            closeEdit();
            // refresh current page
            setPage(0);
        } catch (e) {
            alert("Invalid JSON or server error: " + e.message);
        }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm("Delete this document?")) return;
        try {
            await deleteRecord(selectedColl, doc._id);
            setPage(0);
        } catch (e) {
            alert("Delete failed: " + e.message);
        }
    };

    const treeData = useMemo(() => {
        if (!editJson) return null;

        let obj;
        try {
            obj = JSON.parse(editJson);
        } catch {
            return { error: "Invalid JSON" };
        }

        const MAX_DEPTH = 8; // verhindert zu tiefe Bäume, kann angepasst werden

        const buildNode = (key, value, path, depth) => {
            const nodeId = path; // z.B. "root.user.address.street"
            // Primitive → Leaf
            if (
                depth >= MAX_DEPTH ||
                typeof value !== "object" ||
                value === null ||
                Array.isArray(value)
            ) {
                const display = Array.isArray(value)
                    ? `[${value.length} items]`
                    : JSON.stringify(value);
                return (
                    <TreeItem
                        key={nodeId}
                        itemId={nodeId}
                        label={`${key}: ${display}`}
                    />
                );
            }

            // Objekt → rekursiv
            return (
                <TreeItem key={nodeId} itemId={nodeId} label={key}>
                    {Object.entries(value).map(([k, v]) =>
                        buildNode(k, v, `${path}.${k}`, depth + 1)
                    )}
                </TreeItem>
            );
        };

        return buildNode("root", obj, "root", 0);
    }, [editJson]);

    /* ---------- Render ---------- */
    return (
        <Box sx={{ display: "flex", height: "100%" }}>
            {/* ---- Left: Collection List ---- */}
            <Paper
                sx={{
                    width: 240,
                    flexShrink: 0,
                    borderRight: 1,
                    borderColor: "divider",
                    overflow: "auto",
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6">Collections</Typography>
                </Box>
                <Divider />
                <List dense>
                    {collections.map((c) => (
                        <ListItem key={c} disablePadding>
                            <ListItemButton
                                selected={c === selectedColl}
                                onClick={() => {
                                    setSelectedColl(c);
                                    setPage(0);
                                }}
                            >
                                <ListItemIcon>
                                    <FolderIcon />
                                </ListItemIcon>
                                <ListItemText primary={c} />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Paper>

            {/* ---- Right: Main Content ---- */}
            <Box sx={{ flexGrow: 1, p: 2, overflow: "auto" }}>
                {/* Toolbar */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="h5" sx={{ flexGrow: 1 }}>
                        {selectedColl || "—"}
                    </Typography>

                    <Tooltip title="Refresh">
                        <IconButton
                            onClick={() => {
                                setPage(0);
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Search / Filter */}
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <TextField
                        label="Search"
                        size="small"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Filter (JSON)"
                        size="small"
                        placeholder='{"status":"active"}'
                        value={filter}
                        onChange={(e) => {
                            setFilter(e.target.value);
                            setPage(0);
                        }}
                        sx={{ flexGrow: 1 }}
                    />
                    <Button
                        variant="contained"
                        onClick={() => {
                            // reset everything
                            setSearch("");
                            setFilter("");
                            setSortField("_id");
                            setSortOrder("asc");
                            setPage(0);
                        }}
                    >
                        Reset
                    </Button>
                </Box>

                {/* Table / Tree */}
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : records.length === 0 ? (
                    <Typography>No documents found.</Typography>
                ) : (
                    <>
                        {/* ---- Table Header (clickable for sorting) ---- */}
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {/* Dynamisch die ersten 5 keys (plus _id) anzeigen */}
                                        {["_id", ...Object.keys(records[0] || {})
                                            .filter((k) => k !== "_id")
                                            .slice(0, 4)].map((field) => (
                                            <TableCell
                                                key={field}
                                                onClick={handleSortToggle(field)}
                                                sx={{
                                                    cursor: "pointer",
                                                    userSelect: "none",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    {field}
                                                    {sortField === field ? (
                                                        sortOrder === "asc" ? (
                                                            <ArrowUpward fontSize="small" />
                                                        ) : (
                                                            <ArrowDownward fontSize="small" />
                                                        )
                                                    ) : null}
                                                </Box>
                                            </TableCell>
                                        ))}
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                {/* ---- Body ---- */}
                                <TableBody>
                                    {records.map((doc) => (
                                        <TableRow key={doc._id} hover>
                                            {/* _id cell */}
                                            <TableCell sx={{ maxWidth: 200, overflow: "hidden" }}>
                                                {doc._id}
                                            </TableCell>

                                            {/* Show first few fields (stringified) */}
                                            {Object.keys(doc)
                                                .filter((k) => k !== "_id")
                                                .slice(0, 4)
                                                .map((k) => (
                                                    <TableCell
                                                        key={k}
                                                        sx={{
                                                            maxWidth: 200,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {typeof doc[k] === "object"
                                                            ? JSON.stringify(doc[k])
                                                            : String(doc[k])}
                                                    </TableCell>
                                                ))}

                                            {/* Action buttons */}
                                            <TableCell align="right">
                                                <Tooltip title="Edit (Tree view)">
                                                    <IconButton size="small" onClick={() => openEdit(doc)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(doc)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </>
                )}

                {/* ------------------- Edit Dialog (Tree) ------------------- */}
                <Dialog open={editOpen} onClose={closeEdit} maxWidth="md" fullWidth>
                    <DialogTitle>Edit Document – {editDoc?._id}</DialogTitle>
                    <DialogContent dividers>
                        {/* JSON‑Editor */}
                        <TextField
                            multiline
                            minRows={12}
                            fullWidth
                            value={editJson}
                            onChange={(e) => setEditJson(e.target.value)}
                            variant="outlined"
                        />

                        {/* Tree‑Preview (memoisiert) */}
                        <Box sx={{ mt: 2, maxHeight: 320, overflow: "auto" }}>
                            {treeData ? (
                                typeof treeData === "object" && "error" in treeData ? (
                                    <Typography color="error">{treeData.error}</Typography>
                                ) : (
                                    <SimpleTreeView
                                        sx={{ flexGrow: 1, overflowY: "auto" }}
                                    >
                                        {treeData}
                                    </SimpleTreeView>
                                )
                            ) : null}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeEdit}>Cancel</Button>
                        <Button variant="contained" onClick={saveEdit}>
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
}